/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import {
    Box,
    Card,
    Typography,
    LinearProgress,
    Stack,
    Chip,
    Collapse,
    Alert,
    Button,
    Skeleton,
    IconButton,
    Tooltip,
    AvatarGroup,
    Avatar,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from 'src/components/Iconify';
import { callApi } from 'src/utils/apiCall';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { buildTemplateStatusMap, filterGroupsByCurrentStatus, isTaskOpen } from 'src/utils/checklistViewModel';
import { CompleterAttribution, PausedAttribution, RescheduledChip } from 'src/sections/tasks/_shared/TaskAttribution';
import { parseApiDate, formatTaskDate } from 'src/utils/taskDateUtils';
import { getTaskPauseState } from 'src/utils/taskPauseState';
import { parseWtmResolveResponse } from 'src/components/whatsappComponents/quickReplyUtils';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { error as errorPalette, warning as warningPalette, info as infoPalette, success as successPalette, grey as greyPalette } from 'src/theme/palette';
import { CATEGORY_LABELS, STATUS_COLORS } from 'src/constants/TaskConstants';
import { CHECKLIST_ITEM_TYPE, CHECKLIST_COMPLETION_STATE, TASKS_V2_CHANGED_EVENT, computeItemSummary } from 'src/constants/tasksV2';
import { TASK_EXIT_EVENT, TASK_RESTORE_EVENT } from 'src/sections/tasks/_shared/taskExitAnimation';
import TaskViewer from '../../tasks/TaskViewer';
import IosEmailComposer from '../../tasks/composers/IosEmailComposer';
import IosWhatsAppComposerDialog from '../../tasks/composers/IosWhatsAppComposerDialog';

// -----------------------------------------------

const MAC = {
    cardBg: '#FFFFFF',
    border: 'rgba(0, 0, 0, 0.06)',
    borderHover: 'rgba(0, 0, 0, 0.10)',
    text: '#1D1D1F',
    textSecondary: '#86868B',
    textTertiary: '#A1A1A6',
    accent: '#007AFF',
    success: '#34C759',
    warning: '#FF9500',
    danger: '#FF3B30',
    shadowSm: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)',
    shadowMd: '0 2px 4px rgba(0,0,0,0.06), 0 8px 16px rgba(0,0,0,0.04)',
};
const MAC_FONT_STACK = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", system-ui, sans-serif';

// WCAG AA text colours for alpha-0.10 chip backgrounds — .dark variants pass 4.5:1 on white.
const STATUS_TEXT_COLORS = {
    pending:     warningPalette.dark,   // #B76E00
    in_progress: infoPalette.dark,      // #006C9C
    completed:   successPalette.dark,   // #118D57
    archived:    greyPalette[700],      // #454F5B
};

// -----------------------------------------------

const ACTION_ITEM_TYPES = [CHECKLIST_ITEM_TYPE.EMAIL_SENT, CHECKLIST_ITEM_TYPE.WHATSAPP_SENT];
const ACTION_BUTTON_META = {
    [CHECKLIST_ITEM_TYPE.EMAIL_SENT]:    { label: 'Send Email',    icon: 'eva:email-outline',   color: '#078DEE' },
    [CHECKLIST_ITEM_TYPE.WHATSAPP_SENT]: { label: 'Send WhatsApp', icon: 'ri:whatsapp-fill',    color: '#25D366' },
};

function TemplateGroup({
    group,
    onTaskClick,
    onComposerOpen,
    onDelete,
    defaultExpanded,
    onCompleteAll,
    busy,
    onReopenAll,
    reopenBusy,
    onUnpauseChecklistItem,
    unpausingTaskId,
}) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [menuAnchor, setMenuAnchor] = useState(null);
    // BE no longer creates a parent summary Task (fix shipped 2026-05-25 —
    // TaskService.create_from_template now creates only leaf child Tasks with
    // parent_task=None). All tasks in the group are real action items.
    const visibleTasks = Array.isArray(group.tasks) ? group.tasks : [];
    const total = visibleTasks.length;
    const completed = visibleTasks.filter((t) => t.status === 'completed').length;
    const incompleteCount = visibleTasks.filter(isTaskOpen).length;
    // Tasks that can be reopened: anything not in an open status (completed, skipped, etc.)
    const reopenableCount = visibleTasks.filter((t) => !isTaskOpen(t)).length;
    const progressPct = total > 0 ? (completed / total) * 100 : 0;
    const appliedDate = formatTaskDate(group.applied_at) || '—';
    const appliedBy = group.applied_by?.name || '—';

    return (
        <Box
            sx={{
                border: `1px solid ${MAC.border}`,
                borderRadius: '10px',
                p: '14px',
                bgcolor: MAC.cardBg,
                fontFamily: MAC_FONT_STACK,
                transition: 'border-color 200ms ease',
                '&:hover': { borderColor: MAC.borderHover },
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontFamily: MAC_FONT_STACK, fontWeight: 500, fontSize: '13px', color: MAC.text }}>
                        {group.template_name}
                    </Typography>
                    {group.template_category && CATEGORY_LABELS[group.template_category] && (
                        <Chip
                            label={CATEGORY_LABELS[group.template_category]}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: '11px',
                                fontWeight: 500,
                                bgcolor: 'rgba(0, 122, 255, 0.10)',
                                color: MAC.accent,
                                borderRadius: '6px',
                                '& .MuiChip-label': { px: '8px' },
                            }}
                        />
                    )}
                    {group.template_scope && (
                        <Chip
                            label={group.template_scope}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: '11px',
                                fontWeight: 500,
                                bgcolor: 'rgba(0, 0, 0, 0.05)',
                                color: MAC.textSecondary,
                                borderRadius: '6px',
                                border: 'none',
                                textTransform: 'capitalize',
                                '& .MuiChip-label': { px: '8px' },
                            }}
                        />
                    )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}>
                        <Iconify icon="eva:more-vertical-fill" width={14} sx={{ color: MAC.textTertiary }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                        <Iconify
                            icon="eva:chevron-down-fill"
                            width={14}
                            sx={{ transition: 'transform 200ms ease', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        />
                    </IconButton>
                </Stack>
            </Stack>
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {incompleteCount > 0 && (
                    <MenuItem
                        onClick={() => { setMenuAnchor(null); onCompleteAll(group); }}
                        disabled={busy}
                        sx={{ minWidth: 200 }}
                    >
                        <ListItemIcon>
                            <Iconify icon="eva:checkmark-circle-2-outline" width={18} sx={{ color: MAC.success }} />
                        </ListItemIcon>
                        <ListItemText>{busy ? 'Completing…' : 'Complete all'}</ListItemText>
                    </MenuItem>
                )}
                {reopenableCount > 0 && (
                    <MenuItem
                        onClick={() => { setMenuAnchor(null); onReopenAll(group); }}
                        disabled={reopenBusy}
                        sx={{ minWidth: 200 }}
                    >
                        <ListItemIcon>
                            <Iconify icon="eva:undo-outline" width={18} sx={{ color: MAC.accent }} />
                        </ListItemIcon>
                        <ListItemText>{reopenBusy ? 'Reopening…' : 'Reopen all tasks'}</ListItemText>
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => { setMenuAnchor(null); onDelete(group); }}
                    sx={{ color: 'error.main', minWidth: 200 }}
                >
                    <ListItemIcon sx={{ color: 'error.main' }}>
                        <Iconify icon="eva:trash-2-outline" width={18} />
                    </ListItemIcon>
                    <ListItemText>Remove this checklist</ListItemText>
                </MenuItem>
            </Menu>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <LinearProgress
                    variant="determinate"
                    value={progressPct}
                    sx={{
                        flex: 1,
                        height: 3,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(0, 0, 0, 0.06)',
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 1.5,
                            bgcolor: progressPct === 100 ? MAC.success : MAC.accent,
                            transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                        },
                    }}
                />
                <Typography sx={{ fontFamily: MAC_FONT_STACK, color: MAC.textSecondary, whiteSpace: 'nowrap', fontSize: '11px', fontWeight: 500 }}>
                    {completed} / {total} complete
                </Typography>
            </Stack>
            <Typography sx={{ fontFamily: MAC_FONT_STACK, fontSize: '11px', fontWeight: 400, color: MAC.textTertiary }}>
                Applied {appliedDate} by {appliedBy}
            </Typography>

            <Collapse in={expanded}>
                <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                    {visibleTasks.length === 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                py: 1,
                                px: 1.5,
                                borderRadius: 1,
                                border: '1px dashed',
                                borderColor: 'divider',
                                bgcolor: 'action.hover',
                            }}
                        >
                            <Iconify icon="eva:clock-outline" width={14} sx={{ color: 'text.disabled', flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.disabled', fontStyle: 'italic' }}>
                                Cells unlock once the consultation is attended
                            </Typography>
                        </Box>
                    )}
                    {visibleTasks.map((task) => {
                        // All task/checklist dates go through parseApiDate/formatTaskDate
                        // (handles both DRF "DD-MM-YYYY…Z" and ISO) — never new Date(apiString).
                        const dueDateObj = parseApiDate(task.due_at)?.toDate() || null;
                        const isOverdue = task.status !== 'completed' && dueDateObj && dueDateObj < new Date();
                        const dueLabel = formatTaskDate(task.due_at);
                        const completedLabel = task.status === 'completed' ? formatTaskDate(task.completed_at) : null;
                        const assignees = task.assignees || [];

                        // Paused detection is shared with LeadTaskList and TaskBodyView
                        // so treatment cards, rows, and slideouts agree.
                        const pauseState = getTaskPauseState(task);
                        const taskPaused = pauseState.isPaused;
                        const pausedResumeLabel = taskPaused ? formatTaskDate(pauseState.pausedUntil) : null;
                        const pausedChecklistItemId = pauseState.isChecklistPaused
                            ? pauseState.pauseSource?.id || pauseState.pauseSource?.checklist_item_id
                            : null;
                        const isUnpausing = unpausingTaskId != null && String(unpausingTaskId) === String(task.id);

                        const pendingActionItem = task.checklist_items?.find(
                            (i) => i.completion_state === CHECKLIST_COMPLETION_STATE.PENDING
                                && ACTION_ITEM_TYPES.includes(i.item_type)
                        );
                        const actionMeta = !taskPaused && pendingActionItem ? ACTION_BUTTON_META[pendingActionItem.item_type] : null;

                        return (
                            <Box
                                key={task.id}
                                onClick={() => onTaskClick(task, group)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    py: '8px',
                                    px: '10px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'background-color 150ms ease',
                                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.025)' },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: MAC_FONT_STACK,
                                        flex: 1,
                                        minWidth: 0,
                                        fontSize: '13px',
                                        fontWeight: 400,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        letterSpacing: '-0.01em',
                                        textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                                        color: task.status === 'completed' ? MAC.textSecondary : MAC.text,
                                    }}
                                >
                                    {task.title || '(untitled)'}
                                </Typography>
                                {/* Subtask identifier. Operators reported confusing
                                    template-spawned child Task rows with the
                                    parent Task above them (both render as
                                    similarly-styled rows inside the group).
                                    We surface a small chip ONLY when the row
                                    has a parent_task_id — top-level tasks
                                    stay chip-free to keep the visual quiet
                                    on the common case. The label "Subtask"
                                    is the existing data-model term so we
                                    don't invent new vocabulary. */}
                                {task.parent_task_id && (
                                    <Tooltip
                                        title="Sub-task of the parent task above"
                                        placement="top"
                                        arrow
                                    >
                                        <Chip
                                            label="Subtask"
                                            size="small"
                                            sx={{
                                                height: 20,
                                                fontFamily: MAC_FONT_STACK,
                                                fontSize: '11px',
                                                fontWeight: 500,
                                                bgcolor: 'rgba(0, 0, 0, 0.04)',
                                                color: MAC.textSecondary,
                                                borderRadius: '6px',
                                                flexShrink: 0,
                                                '& .MuiChip-label': { px: '8px' },
                                            }}
                                        />
                                    </Tooltip>
                                )}
                                {task.status && STATUS_COLORS[task.status] && !taskPaused && (
                                    <Chip
                                        label={task.status.replace('_', ' ')}
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontFamily: MAC_FONT_STACK,
                                            fontSize: '11px',
                                            fontWeight: 500,
                                            bgcolor: alpha(STATUS_COLORS[task.status], 0.10),
                                            color: STATUS_TEXT_COLORS[task.status] || STATUS_COLORS[task.status],
                                            textTransform: 'capitalize',
                                            flexShrink: 0,
                                            borderRadius: '6px',
                                            '& .MuiChip-label': { px: '8px' },
                                        }}
                                    />
                                )}
                                {/* Rescheduled chip (hover → who/when when available) */}
                                <RescheduledChip task={task} />
                                {/* Paused row → "[avatar] Paused · resumes {date} · by {who}"
                                    inline in place of the due date; else due date; else
                                    completer attribution. */}
                                {taskPaused ? (
                                    <PausedAttribution pauseSource={pauseState.pauseSource || task} resumeLabel={pausedResumeLabel} size={16} />
                                ) : (
                                    <>
                                        {dueLabel && (
                                            <Typography
                                                variant="caption"
                                                sx={{ fontSize: '0.7rem', color: isOverdue ? errorPalette.main : 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 }}
                                            >
                                                {dueLabel}
                                            </Typography>
                                        )}
                                        {completedLabel && (
                                            <CompleterAttribution task={task} dateLabel={completedLabel} size={16} />
                                        )}
                                    </>
                                )}
                                {taskPaused && pausedChecklistItemId && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={isUnpausing}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onUnpauseChecklistItem(task, pausedChecklistItemId);
                                        }}
                                        sx={{
                                            height: 24,
                                            fontSize: '0.7rem',
                                            textTransform: 'none',
                                            flexShrink: 0,
                                            px: 1,
                                            borderRadius: 1,
                                        }}
                                        startIcon={isUnpausing
                                            ? <Iconify icon="eva:loader-outline" width={12} />
                                            : <Iconify icon="eva:play-circle-outline" width={12} />
                                        }
                                    >
                                        {isUnpausing ? 'Unpausing…' : 'Unpause'}
                                    </Button>
                                )}
                                {assignees.length > 0 && (
                                    <AvatarGroup max={3} sx={{ flexShrink: 0, '& .MuiAvatar-root': { width: 20, height: 20, fontSize: '0.6rem' } }}>
                                        {assignees.map((a, i) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <Tooltip key={i} title={a.name || ''}>
                                                <Avatar src={a.avatar || ''} sx={{ width: 20, height: 20 }}>
                                                    {(a.name || '?')[0]}
                                                </Avatar>
                                            </Tooltip>
                                        ))}
                                    </AvatarGroup>
                                )}
                                {actionMeta && (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onComposerOpen(pendingActionItem, task);
                                        }}
                                        sx={{
                                            height: 24, fontSize: '0.7rem', textTransform: 'none', flexShrink: 0,
                                            bgcolor: actionMeta.color, px: 1, borderRadius: 1,
                                            '&:hover': { bgcolor: actionMeta.color, filter: 'brightness(0.9)' },
                                        }}
                                        startIcon={<Iconify icon={actionMeta.icon} width={12} />}
                                    >
                                        {actionMeta.label}
                                    </Button>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            </Collapse>
        </Box>
    );
}

TemplateGroup.propTypes = {
    group: PropTypes.shape({
        template_id: PropTypes.any,
        template_name: PropTypes.string,
        template_category: PropTypes.string,
        template_scope: PropTypes.string,
        applied_at: PropTypes.string,
        applied_by: PropTypes.shape({ id: PropTypes.any, name: PropTypes.string }),
        tasks: PropTypes.array,
        progress: PropTypes.shape({ completed: PropTypes.number, total: PropTypes.number }),
    }).isRequired,
    onTaskClick: PropTypes.func.isRequired,
    onComposerOpen: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    // Active checklist groups render expanded by default so their tasks are
    // visible without an extra click; completed groups stay collapsed.
    defaultExpanded: PropTypes.bool,
    // Bulk-complete every open task in this checklist (3-dot "Complete all").
    onCompleteAll: PropTypes.func.isRequired,
    // True while this group's bulk-complete request is in flight.
    busy: PropTypes.bool,
    // Bulk-reopen every non-open task in this checklist (3-dot "Reopen all tasks").
    onReopenAll: PropTypes.func.isRequired,
    // True while this group's bulk-reopen request is in flight.
    reopenBusy: PropTypes.bool,
    onUnpauseChecklistItem: PropTypes.func.isRequired,
    unpausingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

TemplateGroup.defaultProps = {
    defaultExpanded: false,
    busy: false,
    reopenBusy: false,
    unpausingTaskId: null,
};

// -----------------------------------------------

export default function TaskChecklistCard({
    practiceId, leadId, leadData, onGroupsLoaded, showCompletedSeparately,
    onNextTaskResolved, onTreatmentTaskSummary, hideCard, onFirstFetchDone,
    groupByTreatment, standaloneOpenTasks, standaloneCompletedTasks, renderStandaloneRow, parentLoading,
    selectedTreatmentId,
}) {
    // Per-treatment section expand/collapse state (groupByTreatment mode).
    // Default open; an explicit `false` collapses.
    const [expandedTreatments, setExpandedTreatments] = useState({});
    // Single global "Show completed" collapse (groupByTreatment mode).
    const [globalCompletedOpen, setGlobalCompletedOpen] = useState(false);
    const [groups, setGroups] = useState([]);
    const hasLoadedOnceRef = useRef(false);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [slideoutOpen, setSlideoutOpen] = useState(false);
    const [selectedTaskId, setSelectedTaskId] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [composerItem, setComposerItem] = useState(null); // { item_type, action_config }
    const [composerOpen, setComposerOpen] = useState(false);
    // WTM (checklist-whatsapp-mappings) resolve result for the item currently open
    // in the composer — { templateId, templateName, itemId } or null (unmapped /
    // not yet resolved). waResolveToken guards against a stale resolve response
    // from a PREVIOUS item landing after the composer has moved on to a new one
    // (mirrors FollowUpPanel's emailResolveToken pattern).
    const [waPrefill, setWaPrefill] = useState(null);
    const waResolveToken = useRef(0);
    const [deleteTarget, setDeleteTarget] = useState(null); // group to delete
    const [deleting, setDeleting] = useState(false);
    const [completingGroupId, setCompletingGroupId] = useState(null); // group being bulk-completed
    const [reopeningGroupId, setReopeningGroupId] = useState(null); // group being bulk-reopened
    const [unpausingTaskId, setUnpausingTaskId] = useState(null);
    // Completed-checklist section starts collapsed on the Tasks tab — it's
    // reference/history, not the active work, so we keep it out of the way.
    const [completedExpanded, setCompletedExpanded] = useState(false);

    const fetchGroups = useCallback(async ({ bustCache = false } = {}) => {
        if (!practiceId || !leadId) return;
        // Match the BE `tasks-v2` permission gate. Without this, lead-detail
        // pages flood Sentry with PermissionDenied 403s on
        // /task-checklists/ for users on practices that don't have tasks-v2
        // (DENTALSEM-CRM-1D6X / 1D7X-series). practiceId is passed so the
        // gate is robust to the practice-switch window.
        if (!hasFeatureAccess('tasks-v2', practiceId)) {
            setGroups([]);
            onGroupsLoaded([]);
            setLoading(false);
            if (!hasLoadedOnceRef.current) { hasLoadedOnceRef.current = true; onFirstFetchDone?.(); }
            return;
        }
        setLoading(true);
        setFetchError(null);
        try {
            // Fetch the lead's checklists AND the practice templates in parallel.
            // Templates give us each template's applies_to_statuses so we can hide
            // stale prior-stage checklists (e.g. a "New Lead Sequence" left open on
            // an ATTENDED lead) via filterGroupsByCurrentStatus. See the util header.
            const [res, tplRes] = await Promise.all([
                callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/task-checklists/`, 'GET', null, false, { bustCache }),
                callApi(`/api/v1/practices/${practiceId}/task-templates/?limit=200`),
            ]);
            if (res.status === 200) {
                let data;
                if (Array.isArray(res.data)) {
                    ({ data } = res);
                } else if (Array.isArray(res.data?.results)) {
                    data = res.data.results;
                } else {
                    data = [];
                }
                let tplData = [];
                if (Array.isArray(tplRes?.data?.results)) tplData = tplRes.data.results;
                else if (Array.isArray(tplRes?.data)) tplData = tplRes.data;
                // Report ALL checklist task ids (pre-filter) to the parent so its
                // "Other tasks" list also excludes the hidden prior-stage tasks —
                // otherwise filtering them out of the card just relocates them into
                // "Other tasks" (LeadTaskList.jsx bucket = tasks not in this set).
                const allChecklistTaskIds = data.flatMap((g) => (g.tasks || []).map((t) => String(t.id)));
                data = filterGroupsByCurrentStatus(data, buildTemplateStatusMap(tplData));
                setGroups(data);
                onGroupsLoaded(allChecklistTaskIds);
            } else if (res.status === 404) {
                setGroups([]);
            } else {
                Sentry.captureMessage('task_checklist_card_unexpected_status', {
                    tags: { status: String(res.status), practice_id: String(practiceId), lead_id: String(leadId) },
                });
                setFetchError('Failed to load task checklists.');
            }
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'task_checklist_card_fetch' } });
            setFetchError('Failed to load task checklists.');
        } finally {
            setLoading(false);
            if (!hasLoadedOnceRef.current) { hasLoadedOnceRef.current = true; onFirstFetchDone?.(); }
        }
    }, [practiceId, leadId, onGroupsLoaded]);

    useEffect(() => {
        fetchGroups();
    }, [practiceId, leadId]);

    // Sibling V2 surfaces (Dashboard V2 TaskChecklistField cell, LeadTaskList)
    // broadcast TASK_EXIT_EVENT / TASK_RESTORE_EVENT on window after a checklist
    // tick flips a task's status. Without this subscription the card stays stale
    // until a manual re-render (route change, refresh). Refetch on either event
    // so the card stays in sync with whichever surface the user just touched.
    useEffect(() => {
        const refetch = () => { fetchGroups({ bustCache: true }); };
        // Guard against re-fetching when the event originated here (TaskViewer
        // onTaskUpdated already called fetchGroups directly — no need to do it
        // again via the event).
        const onLeadTasksChanged = (e) => {
            if (e?.detail?.source === 'checklist-card') return;
            fetchGroups({ bustCache: true });
        };
        window.addEventListener(TASK_EXIT_EVENT, refetch);
        window.addEventListener(TASK_RESTORE_EVENT, refetch);
        window.addEventListener('lead-tasks-v2-changed', onLeadTasksChanged);
        // Also listen on the secondary bus (taskSlice / TaskChecklistField /
        // AttendedGuidedChecklistV2) so cross-bus mutations (e.g. table-cell
        // completions) refresh this card without waiting for a popup close.
        window.addEventListener(TASKS_V2_CHANGED_EVENT, refetch);
        return () => {
            window.removeEventListener(TASK_EXIT_EVENT, refetch);
            window.removeEventListener(TASK_RESTORE_EVENT, refetch);
            window.removeEventListener('lead-tasks-v2-changed', onLeadTasksChanged);
            window.removeEventListener(TASKS_V2_CHANGED_EVENT, refetch);
        };
    }, [fetchGroups]);

    const handleTaskClick = (task, group) => {
        setSelectedTaskId(String(task.id));
        setSelectedGroup(group || null);
        setSlideoutOpen(true);
    };

    // Surface the single soonest-due OPEN task to the parent (the lead Overview
    // "Next step" card). Definition: across every checklist group, the
    // pending/in-progress task with the earliest due_at (an overdue task has the
    // earliest due_at, so it naturally wins). Tasks with no due_at are ignored —
    // the "next" is by due date. Reports { task, group } or null.
    useEffect(() => {
        if (typeof onNextTaskResolved !== 'function') return;
        let best = null;
        groups.forEach((group) => {
            (group.tasks || []).forEach((task) => {
                if (task.status === 'completed' || task.status === 'archived') return;
                if (!task.due_at) return;
                if (!best || (parseApiDate(task.due_at)?.valueOf() ?? Infinity) < (parseApiDate(best.task.due_at)?.valueOf() ?? Infinity)) {
                    best = { task, group };
                }
            });
        });
        onNextTaskResolved(best);
    }, [groups, onNextTaskResolved]);

    // Emit a per-treatment task summary map to the parent Overview so it can
    // drive task-aware Next Step cards for each treatment independently.
    // Shape: { [leadTreatmentId]: { total, open, done, nextOpenTask } }
    // Tasks with no linked treatment (lead_treatment === null) are bucketed
    // under '_unscoped'; the parent merges them into finalTreatmentArray[0].
    // nextOpenTask: the soonest-due open task for that treatment bucket, or
    // null when no open tasks have a due_at.
    useEffect(() => {
        if (typeof onTreatmentTaskSummary !== 'function') return;
        const summaryMap = {};
        groups.forEach((group) => {
            (group.tasks || []).forEach((task) => {
                if (task.status === 'archived') return;
                const ltId = task.lead_treatment != null ? String(task.lead_treatment.id) : '_unscoped';
                if (!summaryMap[ltId]) {
                    summaryMap[ltId] = {
                        total: 0, open: 0, done: 0, paused: 0, pausedUntil: null, nextOpenTask: null,
                        // Item-level work/touchpoint signals — aggregated across all tasks.
                        // hasPausedWork: any WORK item is PAUSED → lead on hold.
                        // hasPausedTouchpoints: any TOUCHPOINT item is PAUSED → follow-up scheduled.
                        // _workCount / _workDoneCount: used to compute allWorkDone in OverView.
                        hasPausedWork: false, hasPausedTouchpoints: false, _workCount: 0, _workDoneCount: 0,
                    };
                }
                summaryMap[ltId].total += 1;
                const items = task.checklist_items || [];
                // Aggregate item-level signals across every task (not just paused ones).
                const r = computeItemSummary(items);
                if (r.hasPausedWork) summaryMap[ltId].hasPausedWork = true;
                if (r.hasPausedTouchpoints) summaryMap[ltId].hasPausedTouchpoints = true;
                summaryMap[ltId]._workCount += r.workCount;
                summaryMap[ltId]._workDoneCount += r.workDoneCount;
                const pauseState = getTaskPauseState(task);
                const isPausedTask = pauseState.isPaused;
                if (isPausedTask) {
                    summaryMap[ltId].paused += 1;
                    const d = parseApiDate(pauseState.pausedUntil)?.valueOf();
                    const cur = parseApiDate(summaryMap[ltId].pausedUntil)?.valueOf();
                    if (d != null && (cur == null || d < cur)) {
                        summaryMap[ltId].pausedUntil = pauseState.pausedUntil;
                    }
                } else {
                    const isOpen = task.status === 'pending' || task.status === 'in_progress';
                    if (isOpen) {
                        summaryMap[ltId].open += 1;
                        if (task.due_at) {
                            const current = summaryMap[ltId].nextOpenTask;
                            if (!current || (parseApiDate(task.due_at)?.valueOf() ?? Infinity) < (parseApiDate(current.due_at)?.valueOf() ?? Infinity)) {
                                summaryMap[ltId].nextOpenTask = task;
                            }
                        }
                    } else {
                        summaryMap[ltId].done += 1;
                    }
                }
            });
        });
        onTreatmentTaskSummary(summaryMap);
    }, [groups, onTreatmentTaskSummary]);

    const handleSlideoutClose = () => {
        setSlideoutOpen(false);
        setSelectedTaskId(null);
        setSelectedGroup(null);
    };

    const handleSlideoutDeleteChecklist = () => {
        handleSlideoutClose();
        if (selectedGroup) handleDeleteGroup(selectedGroup);
    };

    // WHATSAPP_SENT items may map to a per-practice WhatsApp template (WTM —
    // checklist-whatsapp-mappings). When mapped, resolve it here so the composer
    // opens with the template already loaded (matches FollowUpPanel /
    // ActionPanel's resolveMappingForChannel pattern) instead of the blank
    // picker. Unmapped items (no template_item) skip the request entirely.
    const resolveWaTemplateForItem = (item) => {
        const templateItemId = item?.template_item;
        if (!templateItemId) return;
        const resolvePracticeId = leadData?.practice || leadData?.practice_id || practiceId;
        if (!resolvePracticeId) return;
        const itemId = item?.id ?? templateItemId;
        waResolveToken.current += 1;
        const myToken = waResolveToken.current;
        callApi(`/api/v1/practices/${resolvePracticeId}/checklist-whatsapp-mappings/resolve/?template_checklist_item=${templateItemId}`)
            .then((res) => {
                // Stale-response guard: ignore if the composer has since moved on
                // to a different item (or closed) since this request was fired.
                if (waResolveToken.current !== myToken) return;
                const wtm = parseWtmResolveResponse(res);
                if (wtm) setWaPrefill({ templateId: wtm.id, templateName: wtm.name, itemId });
            })
            .catch(() => { /* silent — composer stays on the plain template picker */ });
    };

    const handleComposerOpen = (item) => {
        setComposerItem(item);
        setComposerOpen(true);
        setWaPrefill(null);
        if (item?.item_type === CHECKLIST_ITEM_TYPE.WHATSAPP_SENT) {
            resolveWaTemplateForItem(item);
        }
    };

    const handleComposerClose = () => {
        setComposerOpen(false);
        setComposerItem(null);
        setWaPrefill(null);
        // Invalidate any in-flight resolve so a late response can't set prefill
        // state after the composer has already closed.
        waResolveToken.current += 1;
    };

    const handleComposerSend = () => {
        handleComposerClose();
        fetchGroups();
    };

    const handleDeleteGroup = (group) => {
        setDeleteTarget(group);
    };

    // "Complete all" — mark every still-open task in a checklist done in one go.
    // Reuses the per-task complete endpoint (no bulk endpoint exists); available
    // to all practice members (BE gate is IsSamePracticeMember). Terminal tasks
    // (completed/skipped/archived) are left untouched. Once every task is
    // completed the group falls into the "Completed Task Checklists" section.
    const handleCompleteAllGroup = async (group) => {
        const openTasks = (group?.tasks || []).filter(isTaskOpen);
        if (!openTasks.length) return;
        setCompletingGroupId(group.template_id ?? group.id ?? 'group');
        try {
            const results = await Promise.all(
                // force: deliberate "mark these done" — skip the required-checklist
                // gate so an unfinished item row doesn't block completion (matches
                // TaskBulkActionBar). 204 is a success status for this endpoint.
                openTasks.map((t) =>
                    callApi(`/api/v1/practices/${practiceId}/tasks/${t.id}/complete/`, 'post', { force: true })
                        .then((r) => ({ ok: r?.status === 200 || r?.status === 204 }), () => ({ ok: false }))
                )
            );
            const ok = results.filter((r) => r.ok).length;
            const failed = results.length - ok;
            dispatch(updateError({
                success: failed === 0,
                message: failed === 0
                    ? `Completed ${ok} task${ok !== 1 ? 's' : ''}`
                    : `Completed ${ok} of ${results.length} — ${failed} failed`,
            }));
            await fetchGroups();
            // Refresh the lead-detail task badge / contact-attempt pill + sibling surfaces.
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('lead-tasks-v2-changed', { detail: { source: 'complete-all' } }));
            }
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'checklist_complete_all' } });
            dispatch(updateError({ success: false, message: 'Failed to complete checklist.' }));
        } finally {
            setCompletingGroupId(null);
        }
    };

    const isGroupBusy = (g) => completingGroupId != null
        && String(completingGroupId) === String(g?.template_id ?? g?.id ?? 'group');

    const handleUnpauseChecklistItem = async (task, itemId) => {
        if (!task?.id || !itemId || !practiceId) return;
        setUnpausingTaskId(task.id);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${task.id}/checklist/${itemId}/reopen/`,
                'post'
            );
            if (res?.status === 200 || res?.status === 204) {
                dispatch(updateError({ success: true, message: 'Checklist resumed.' }));
                await fetchGroups({ bustCache: true });
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('lead-tasks-v2-changed', { detail: { source: 'checklist-unpause' } }));
                }
            } else {
                dispatch(updateError({ success: false, message: 'Failed to unpause checklist.' }));
            }
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'checklist_unpause_item' } });
            dispatch(updateError({ success: false, message: 'Failed to unpause checklist.' }));
        } finally {
            setUnpausingTaskId(null);
        }
    };

    // "Reopen all" — mark every completed/skipped task in a checklist back to pending.
    // Mirrors handleCompleteAllGroup but targets non-open tasks and calls the reopen
    // endpoint (POST /tasks/{id}/reopen/) — no `force` flag, no body required.
    const handleReopenAllGroup = async (group) => {
        const closedTasks = (group?.tasks || []).filter((t) => !isTaskOpen(t));
        if (!closedTasks.length) return;
        setReopeningGroupId(group.template_id ?? group.id ?? 'group');
        try {
            const results = await Promise.all(
                closedTasks.map((t) =>
                    callApi(`/api/v1/practices/${practiceId}/tasks/${t.id}/reopen/`, 'post')
                        .then((r) => ({ ok: r?.status === 200 || r?.status === 204 }), () => ({ ok: false }))
                )
            );
            const ok = results.filter((r) => r.ok).length;
            const failed = results.length - ok;
            dispatch(updateError({
                success: failed === 0,
                message: failed === 0
                    ? `Reopened ${ok} task${ok !== 1 ? 's' : ''}`
                    : `Reopened ${ok} of ${results.length} — ${failed} failed`,
            }));
            await fetchGroups();
            // Refresh the lead-detail task badge / contact-attempt pill + sibling surfaces.
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('lead-tasks-v2-changed', { detail: { source: 'reopen-all' } }));
            }
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'checklist_reopen_all' } });
            dispatch(updateError({ success: false, message: 'Failed to reopen tasks.' }));
        } finally {
            setReopeningGroupId(null);
        }
    };

    const isGroupReopenBusy = (g) => reopeningGroupId != null
        && String(reopeningGroupId) === String(g?.template_id ?? g?.id ?? 'group');

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        const tasks = deleteTarget.tasks || [];
        if (!tasks.length) {
            dispatch(updateError({ success: false, message: 'Unable to identify checklist tasks.' }));
            setDeleteTarget(null);
            return;
        }
        setDeleting(true);
        try {
            const results = await Promise.all(
                tasks.map((t) =>
                    callApi(`/api/v1/practices/${practiceId}/tasks/${t.id}/`, 'delete')
                        .then((r) => ({ ok: r?.status === 204 || r?.status === 200 }), () => ({ ok: false }))
                )
            );
            const ok = results.filter((r) => r.ok).length;
            const failed = results.length - ok;
            if (failed === 0) {
                setDeleteTarget(null);
            } else {
                dispatch(updateError({
                    success: false,
                    message: `Removed ${ok} of ${results.length} tasks — ${failed} failed`,
                }));
            }
            await fetchGroups();
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('lead-tasks-v2-changed', { detail: { source: 'remove-checklist' } }));
            }
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'delete_applied_checklist' } });
            dispatch(updateError({ success: false, message: 'Failed to remove checklist.' }));
        } finally {
            setDeleting(false);
        }
    };

    const cardSx = {
        p: '14px',
        mb: 1.5,
        borderRadius: '10px',
        border: `1px solid ${MAC.border}`,
        boxShadow: MAC.shadowSm,
        bgcolor: MAC.cardBg,
        fontFamily: MAC_FONT_STACK,
        transition: 'box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': { boxShadow: MAC.shadowMd },
    };
    const headingSx = {
        fontFamily: MAC_FONT_STACK,
        fontWeight: 500,
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: MAC.textSecondary,
        mb: 1.5,
    };

    // Headless mode (lead Overview): the data effects above still run — fetching
    // groups and reporting the soonest-due task via onNextTaskResolved to power
    // the Overview "Next step" card — but nothing is rendered here, because the
    // full "Active Task Checklists" card already lives on the Tasks tab. This
    // avoids duplicating it on the Overview.
    if (hideCard) return null;

    if (loading) {
        return (
            <Card sx={cardSx}>
                <Typography variant="subtitle2" sx={headingSx}>Active Task Checklists</Typography>
                <Stack spacing={1}>
                    {[1, 2].map((i) => <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: 1.5 }} />)}
                </Stack>
            </Card>
        );
    }

    if (fetchError) {
        return (
            <Card sx={cardSx}>
                <Typography variant="subtitle2" sx={headingSx}>Active Task Checklists</Typography>
                <Alert severity="warning" action={<Button size="small" onClick={fetchGroups}>Retry</Button>}>
                    {fetchError}
                </Alert>
            </Card>
        );
    }

    // groupByTreatment mode handles its own empty state (it may still have
    // standalone tasks even when there are no template checklist groups), so we
    // skip this template-only empty card in that mode.
    if (groups.length === 0 && !groupByTreatment) {
        return (
            <Card sx={cardSx}>
                <Typography variant="subtitle2" sx={headingSx}>Active Task Checklists</Typography>
                <Box sx={{ py: 2.5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
                    <Typography variant="caption" color="text.disabled">
                        No task checklists applied to this lead. Apply a template from the Tasks section to track structured workflows.
                    </Typography>
                </Box>
            </Card>
        );
    }

    // A checklist group is "complete" when it has cells AND every task is
    // completed. Groups with no tasks yet (cells unlock once the consultation is
    // attended) stay in the active list. Completed groups are hidden from the
    // Overview entirely (cleaner UI) and shown in their own section in the Tasks
    // tab (showCompletedSeparately).
    const isGroupComplete = (g) => {
        const tasks = Array.isArray(g.tasks) ? g.tasks : [];
        return tasks.length > 0 && tasks.every((t) => t.status === 'completed');
    };
    const activeGroups = groups.filter((g) => !isGroupComplete(g));
    const completedGroups = groups.filter(isGroupComplete);

    // ── Group-by-treatment mode ────────────────────────────────────────────
    // Bucket the ACTIVE template groups and the standalone OPEN tasks by
    // treatment, render the full TemplateGroup (3-dot menu + inline items) and
    // the standalone TaskRows together under each treatment header, and collect
    // every completed item (template groups + standalone) into ONE global
    // collapse. The "No treatment" bucket sorts last.
    if (groupByTreatment) {
        const NO_TREATMENT_KEY = 'none';
        const NO_TREATMENT_NAME = 'No treatment';

        // Human-readable label for a lead treatment status (e.g. "NEW" → "New").
        // Covers the common lead status values — unknown strings fall back to
        // title-cased form so we never render raw SCREAMING_SNAKE.
        const treatmentStatusLabel = (s) => {
            if (!s) return null;
            const MAP = {
                NEW: 'New', CONSULTATION_BOOKED: 'Booked', CONSULTATION_ATTENDED: 'Attended',
                TREATMENT_STARTED: 'Treatment started', CLOSED: 'Closed',
                FAILED_TO_ATTEND: 'FTA', ARCHIVED: 'Archived',
            };
            return MAP[String(s).toUpperCase()] || String(s).replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
        };

        // A template group's treatment = the treatment of its first task that
        // has one (a template instance maps to a single treatment; if tasks
        // somehow span treatments we bucket by the first non-null).
        const groupTreatment = (g) => {
            const tasks = Array.isArray(g.tasks) ? g.tasks : [];
            const withTreatment = tasks.find((t) => t.lead_treatment != null);
            if (!withTreatment) return { id: NO_TREATMENT_KEY, name: NO_TREATMENT_NAME, status: null, price: null };
            const lt = withTreatment.lead_treatment;
            return {
                id: String(lt.id),
                name: lt.treatment_name || NO_TREATMENT_NAME,
                status: lt.status || null,
                // custom_price overrides price (user-set fee); price is the template default.
                price: lt.custom_price != null ? lt.custom_price : (lt.price ?? null),
            };
        };
        const taskTreatment = (t) => {
            if (t.lead_treatment == null) return { id: NO_TREATMENT_KEY, name: NO_TREATMENT_NAME, status: null, price: null };
            const lt = t.lead_treatment;
            return {
                id: String(lt.id),
                name: lt.treatment_name || NO_TREATMENT_NAME,
                status: lt.status || null,
                price: lt.custom_price != null ? lt.custom_price : (lt.price ?? null),
            };
        };

        // Count open + paused tasks in a template group for the section chip.
        const isTaskPaused = (t) => getTaskPauseState(t).isPaused;

        // Build buckets: { [treatmentId]: { id, name, status, price, groups: [], standalone: [] } }
        const buckets = new Map();
        const ensureBucket = ({ id, name, status, price }) => {
            if (!buckets.has(id)) buckets.set(id, { id, name, status, price, groups: [], standalone: [] });
            const b = buckets.get(id);
            if (name !== NO_TREATMENT_NAME && b.name === NO_TREATMENT_NAME) b.name = name;
            if (!b.status && status) b.status = status;
            if (b.price == null && price != null) b.price = price;
            return b;
        };
        // Step 1: seed active treatments from lead data so treatments with zero
        // active tasks still get a section (e.g. Perio on lead 131252).
        // Closed and archived treatments are excluded — they have no open tasks
        // to act on and would create ghost sections in the UI.
        const INACTIVE_STATUSES = new Set(['CLOSED', 'ARCHIVED']);
        (leadData?.lead_treatments ?? [])
            .filter((lt) => !INACTIVE_STATUSES.has(lt.status))
            .forEach((lt) => {
                ensureBucket({
                    id: String(lt.id),
                    name: lt.treatment_name || lt.treatment || NO_TREATMENT_NAME,
                    status: lt.status || null,
                    price: lt.custom_price != null ? lt.custom_price : (lt.price ?? null),
                });
            });
        // Step 2: fill buckets with active template groups and standalone tasks.
        activeGroups.forEach((g) => { ensureBucket(groupTreatment(g)).groups.push(g); });
        (standaloneOpenTasks || []).forEach((t) => { ensureBucket(taskTreatment(t)).standalone.push(t); });

        // Per-bucket counts for the "{open} open · {paused} paused" chip.
        const bucketCounts = (b) => {
            let open = 0;
            let paused = 0;
            b.groups.forEach((g) => {
                (g.tasks || []).forEach((t) => {
                    if (t.status === 'completed' || t.status === 'archived') return;
                    if (isTaskPaused(t)) paused += 1;
                    else open += 1;
                });
            });
            b.standalone.forEach((t) => {
                if (isTaskPaused(t)) paused += 1;
                else open += 1;
            });
            return { open, paused };
        };

        const orderedBuckets = Array.from(buckets.values())
            .sort((a, b) => {
                if (a.id === NO_TREATMENT_KEY) return 1;
                if (b.id === NO_TREATMENT_KEY) return -1;
                return a.name.localeCompare(b.name);
            });

        const completedStandalone = standaloneCompletedTasks || [];
        const totalCompleted = completedGroups.reduce((n, g) => n + (g.tasks?.length || 0), 0) + completedStandalone.length;
        const isExpanded = (key) => expandedTreatments[key] !== false;
        const toggle = (key) => setExpandedTreatments((prev) => ({ ...prev, [key]: prev[key] === false }));

        const busyLoading = loading || parentLoading;
        // hasAnything: true when there are any treatment sections (even zero-task
        // ones seeded from lead_treatments) or any completed items to show.
        const hasAnything = orderedBuckets.length > 0 || totalCompleted > 0;

        // Compute segmented filter display. When the URL has a selected
        // treatment, scope the top summary chips to that treatment so a paused
        // sibling/null bucket cannot make the selected treatment look paused.
        const summaryBuckets = selectedTreatmentId
            ? orderedBuckets.filter((b) => String(b.id) === String(selectedTreatmentId))
            : orderedBuckets;
        const allOpenCount = summaryBuckets.reduce((n, b) => n + bucketCounts(b).open, 0);
        const allPausedCount = summaryBuckets.reduce((n, b) => n + bucketCounts(b).paused, 0);

        return (
            <Box sx={{ fontFamily: MAC_FONT_STACK }}>
                {busyLoading && !hasAnything && (
                    <Stack spacing={1}>
                        {[1, 2].map((i) => <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: 1.5 }} />)}
                    </Stack>
                )}

                {fetchError && (
                    <Alert severity="warning" sx={{ mb: 1.5 }} action={<Button size="small" onClick={fetchGroups}>Retry</Button>}>
                        {fetchError}
                    </Alert>
                )}

                {/* Segmented summary bar: quick glance at open vs paused across all treatments. */}
                {hasAnything && !busyLoading && (allOpenCount > 0 || allPausedCount > 0) && (
                    <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                        {allOpenCount > 0 && (
                            <Chip
                                label={`${allOpenCount} open`}
                                size="small"
                                sx={{
                                    height: 22, fontFamily: MAC_FONT_STACK, fontSize: '11px', fontWeight: 500,
                                    bgcolor: alpha(successPalette.main, 0.10), color: successPalette.dark,
                                    borderRadius: '6px', '& .MuiChip-label': { px: '8px' },
                                }}
                            />
                        )}
                        {allPausedCount > 0 && (
                            <Chip
                                label={`${allPausedCount} paused`}
                                size="small"
                                sx={{
                                    height: 22, fontFamily: MAC_FONT_STACK, fontSize: '11px', fontWeight: 500,
                                    bgcolor: alpha(warningPalette.main, 0.10), color: warningPalette.dark,
                                    borderRadius: '6px', '& .MuiChip-label': { px: '8px' },
                                }}
                            />
                        )}
                        {totalCompleted > 0 && (
                            <Chip
                                label={`${totalCompleted} completed`}
                                size="small"
                                sx={{
                                    height: 22, fontFamily: MAC_FONT_STACK, fontSize: '11px', fontWeight: 500,
                                    bgcolor: 'rgba(0, 0, 0, 0.05)', color: MAC.textSecondary,
                                    borderRadius: '6px', '& .MuiChip-label': { px: '8px' },
                                }}
                            />
                        )}
                    </Stack>
                )}

                {orderedBuckets.map((bucket) => {
                    const { open, paused } = bucketCounts(bucket);
                    const completed = completedGroups
                        .filter((g) => {
                            const t = groupTreatment(g);
                            return t.id === bucket.id;
                        })
                        .reduce((n, g) => n + (g.tasks?.length || 0), 0)
                        + (completedStandalone.filter((t) => taskTreatment(t).id === bucket.id).length);
                    const expanded = isExpanded(bucket.id);
                    const statusLabel = treatmentStatusLabel(bucket.status);
                    const priceLabel = bucket.price != null ? `£${Number(bucket.price).toLocaleString('en-GB')}` : null;
                    return (
                        <Box key={bucket.id} sx={{ mt: 2 }}>
                            <Box
                                onClick={() => toggle(bucket.id)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                                    mb: 1, py: '4px', borderRadius: '6px', flexWrap: 'wrap',
                                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
                                }}
                            >
                                <Iconify
                                    icon="eva:chevron-down-fill"
                                    width={16}
                                    sx={{ color: MAC.textTertiary, transition: 'transform 200ms ease', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', flexShrink: 0 }}
                                />
                                <Typography sx={{
                                    fontFamily: MAC_FONT_STACK, fontWeight: 600, fontSize: '13px', color: MAC.text,
                                    letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                    {bucket.name}
                                </Typography>
                                {statusLabel && (
                                    <Chip
                                        label={statusLabel}
                                        size="small"
                                        sx={{
                                            height: 18, fontFamily: MAC_FONT_STACK, fontSize: '10px', fontWeight: 500,
                                            bgcolor: 'rgba(0, 122, 255, 0.08)', color: infoPalette.dark, borderRadius: '5px',
                                            flexShrink: 0, '& .MuiChip-label': { px: '6px' },
                                        }}
                                    />
                                )}
                                {priceLabel && (
                                    <Typography sx={{ fontFamily: MAC_FONT_STACK, fontSize: '11px', fontWeight: 500, color: MAC.textSecondary, flexShrink: 0 }}>
                                        {priceLabel}
                                    </Typography>
                                )}
                                {/* open · paused · completed counts */}
                                <Typography sx={{ fontFamily: MAC_FONT_STACK, fontSize: '11px', color: MAC.textTertiary, ml: 'auto', flexShrink: 0 }}>
                                    {[
                                        open > 0 ? `${open} open` : null,
                                        paused > 0 ? `${paused} paused` : null,
                                        completed > 0 ? `${completed} done` : null,
                                    ].filter(Boolean).join(' · ') || 'No tasks'}
                                </Typography>
                            </Box>
                            <Collapse in={expanded} timeout={250}>
                                <Stack spacing={1}>
                                    {bucket.groups.map((group, i) => (
                                        <TemplateGroup
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={`${group.template_id}-grp-${i}`}
                                            group={group}
                                            onTaskClick={handleTaskClick}
                                            onComposerOpen={handleComposerOpen}
                                            onDelete={handleDeleteGroup}
                                            onCompleteAll={handleCompleteAllGroup}
                                            busy={isGroupBusy(group)}
                                            onReopenAll={handleReopenAllGroup}
                                            reopenBusy={isGroupReopenBusy(group)}
                                            onUnpauseChecklistItem={handleUnpauseChecklistItem}
                                            unpausingTaskId={unpausingTaskId}
                                            defaultExpanded
                                        />
                                    ))}
                                    {bucket.standalone.length > 0 && typeof renderStandaloneRow === 'function' && (
                                        <Box sx={{
                                            bgcolor: MAC.cardBg, border: `1px solid ${MAC.border}`, borderRadius: '10px',
                                            boxShadow: MAC.shadowSm, p: '6px',
                                        }}>
                                            {bucket.standalone.map((t) => (
                                                <Box key={`standalone-${t.id}`}>{renderStandaloneRow(t)}</Box>
                                            ))}
                                        </Box>
                                    )}
                                    {/* Compact empty state for treatments seeded from lead data
                                        that have no active tasks yet (e.g. a treatment plan
                                        exists but no checklist has been applied). Keeps the
                                        section present so the operator knows the treatment is
                                        tracked; one small dashed line, not a big card. */}
                                    {bucket.groups.length === 0 && bucket.standalone.length === 0 && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                py: 1,
                                                px: 1.5,
                                                borderRadius: 1,
                                                border: '1px dashed',
                                                borderColor: 'divider',
                                                bgcolor: 'action.hover',
                                            }}
                                        >
                                            <Iconify icon="eva:clock-outline" width={14} sx={{ color: 'text.disabled', flexShrink: 0 }} />
                                            <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.disabled', fontStyle: 'italic' }}>
                                                No open tasks for {bucket.name}
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </Collapse>
                        </Box>
                    );
                })}

                {totalCompleted > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Button
                            size="small"
                            variant="text"
                            endIcon={<Iconify icon="eva:chevron-down-fill" width={14} sx={{ transition: 'transform 200ms ease', transform: globalCompletedOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />}
                            onClick={() => setGlobalCompletedOpen((v) => !v)}
                            sx={{
                                fontFamily: MAC_FONT_STACK, fontSize: '11px', fontWeight: 500, color: MAC.textSecondary,
                                textTransform: 'none', p: '4px 8px', minWidth: 0,
                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.03)', color: MAC.text },
                            }}
                        >
                            {globalCompletedOpen ? 'Hide' : 'Show'} completed ({totalCompleted})
                        </Button>
                        <Collapse in={globalCompletedOpen} timeout={250}>
                            <Stack spacing={1} sx={{ mt: 0.75 }}>
                                {completedGroups.map((group, i) => (
                                    <TemplateGroup
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`${group.template_id}-done-${i}`}
                                        group={group}
                                        onTaskClick={handleTaskClick}
                                        onComposerOpen={handleComposerOpen}
                                        onDelete={handleDeleteGroup}
                                        onCompleteAll={handleCompleteAllGroup}
                                        busy={isGroupBusy(group)}
                                        onReopenAll={handleReopenAllGroup}
                                        reopenBusy={isGroupReopenBusy(group)}
                                        onUnpauseChecklistItem={handleUnpauseChecklistItem}
                                        unpausingTaskId={unpausingTaskId}
                                    />
                                ))}
                                {completedStandalone.length > 0 && typeof renderStandaloneRow === 'function' && (
                                    <Box sx={{
                                        bgcolor: MAC.cardBg, border: `1px solid ${MAC.border}`, borderRadius: '10px',
                                        boxShadow: MAC.shadowSm, p: '6px',
                                    }}>
                                        {completedStandalone.map((t) => (
                                            <Box key={`standalone-done-${t.id}`}>{renderStandaloneRow(t)}</Box>
                                        ))}
                                    </Box>
                                )}
                            </Stack>
                        </Collapse>
                    </Box>
                )}

                {!hasAnything && !busyLoading && (
                    <Box sx={{ py: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
                        <Iconify icon="eva:checkmark-circle-2-outline" width={28} sx={{ color: 'text.disabled', mb: 0.5 }} />
                        <Typography sx={{ fontFamily: MAC_FONT_STACK, fontSize: '12px', color: MAC.textTertiary }}>
                            No tasks yet for this lead.
                        </Typography>
                        <Typography sx={{ fontFamily: MAC_FONT_STACK, fontSize: '11px', color: MAC.textTertiary, mt: 0.25 }}>
                            Apply a checklist template or add a task above.
                        </Typography>
                    </Box>
                )}

                {/* Slideout + dialogs + composers (shared with the default render
                    path below). Mounted here so grouped mode keeps full template
                    task viewing / deletion / composer behaviour. */}
                <TaskViewer
                    open={slideoutOpen}
                    onClose={handleSlideoutClose}
                    taskId={selectedTaskId}
                    practiceId={practiceId}
                    onTaskUpdated={() => {
                        fetchGroups();
                        window.dispatchEvent(new CustomEvent('lead-tasks-v2-changed', { detail: { source: 'checklist-card' } }));
                    }}
                    onDeleteChecklist={selectedGroup ? handleSlideoutDeleteChecklist : null}
                />
                <Dialog
                    open={Boolean(deleteTarget)}
                    onClose={() => !deleting && setDeleteTarget(null)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>Remove this applied checklist?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            This will permanently remove <strong>{deleteTarget?.template_name}</strong> from this lead.
                            The template itself won&apos;t be deleted — only this lead&apos;s instance will be removed.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
                            Cancel
                        </Button>
                        <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleting}>
                            {deleting ? 'Removing…' : 'Remove'}
                        </Button>
                    </DialogActions>
                </Dialog>
                {composerOpen && composerItem?.item_type === CHECKLIST_ITEM_TYPE.EMAIL_SENT && (
                    <IosEmailComposer
                        open={composerOpen}
                        onClose={handleComposerClose}
                        practiceId={practiceId}
                        leadId={leadId}
                        leadDetail={leadData || { id: leadId }}
                        templateId={composerItem.action_config?.template_id || null}
                        onSendSuccess={handleComposerSend}
                    />
                )}
                {composerOpen && composerItem?.item_type === CHECKLIST_ITEM_TYPE.WHATSAPP_SENT && (
                    <IosWhatsAppComposerDialog
                        open={composerOpen}
                        onClose={handleComposerClose}
                        practiceId={practiceId}
                        leadId={leadId}
                        leadDetail={leadData || { id: leadId }}
                        phoneNumber={leadData?.lead_contact?.phone || leadData?.phone || ''}
                        onSendSuccess={handleComposerSend}
                        prefillWaTemplateId={waPrefill?.templateId ?? null}
                        prefillWaTemplateName={waPrefill?.templateName ?? null}
                        prefillWaItemId={waPrefill?.itemId ?? null}
                    />
                )}
            </Box>
        );
    }

    return (
        <>
            <Card sx={cardSx}>
                <Typography variant="subtitle2" sx={headingSx}>Active Task Checklists</Typography>
                {activeGroups.length === 0 ? (
                    <Box sx={{ py: 2.5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
                        <Typography variant="caption" color="text.disabled">
                            {completedGroups.length > 0
                                ? 'All task checklists complete.'
                                : 'No active task checklists.'}
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={1}>
                        {activeGroups.map((group, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <TemplateGroup
                                key={`${group.template_id}-active-${i}`}
                                group={group}
                                onTaskClick={handleTaskClick}
                                onComposerOpen={handleComposerOpen}
                                onDelete={handleDeleteGroup}
                                onCompleteAll={handleCompleteAllGroup}
                                busy={isGroupBusy(group)}
                                onReopenAll={handleReopenAllGroup}
                                reopenBusy={isGroupReopenBusy(group)}
                                onUnpauseChecklistItem={handleUnpauseChecklistItem}
                                unpausingTaskId={unpausingTaskId}
                                defaultExpanded
                            />
                        ))}
                    </Stack>
                )}
            </Card>
            {showCompletedSeparately && completedGroups.length > 0 && (
                <Card sx={cardSx}>
                    {/* Collapsible — starts closed. Click the header to expand. */}
                    <Box
                        onClick={() => setCompletedExpanded((v) => !v)}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                    >
                        <Typography variant="subtitle2" sx={{ ...headingSx, mb: 0 }}>
                            Completed Task Checklists ({completedGroups.length})
                        </Typography>
                        <Iconify
                            icon="eva:chevron-down-fill"
                            width={16}
                            sx={{ color: MAC.textTertiary, transition: 'transform 200ms ease', transform: completedExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        />
                    </Box>
                    <Collapse in={completedExpanded}>
                        <Stack spacing={1} sx={{ mt: 1.5 }}>
                            {completedGroups.map((group, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <TemplateGroup
                                    key={`${group.template_id}-done-${i}`}
                                    group={group}
                                    onTaskClick={handleTaskClick}
                                    onComposerOpen={handleComposerOpen}
                                    onDelete={handleDeleteGroup}
                                    onCompleteAll={handleCompleteAllGroup}
                                    busy={isGroupBusy(group)}
                                    onReopenAll={handleReopenAllGroup}
                                    reopenBusy={isGroupReopenBusy(group)}
                                    onUnpauseChecklistItem={handleUnpauseChecklistItem}
                                    unpausingTaskId={unpausingTaskId}
                                />
                            ))}
                        </Stack>
                    </Collapse>
                </Card>
            )}
            <TaskViewer
                open={slideoutOpen}
                onClose={handleSlideoutClose}
                taskId={selectedTaskId}
                practiceId={practiceId}
                onTaskUpdated={() => {
                    fetchGroups();
                    window.dispatchEvent(new CustomEvent('lead-tasks-v2-changed', { detail: { source: 'checklist-card' } }));
                }}
                onDeleteChecklist={selectedGroup ? handleSlideoutDeleteChecklist : null}
            />
            <Dialog
                open={Boolean(deleteTarget)}
                onClose={() => !deleting && setDeleteTarget(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Remove this applied checklist?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will permanently remove <strong>{deleteTarget?.template_name}</strong> from this lead.
                        The template itself won&apos;t be deleted — only this lead&apos;s instance will be removed.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                        disabled={deleting}
                    >
                        {deleting ? 'Removing…' : 'Remove'}
                    </Button>
                </DialogActions>
            </Dialog>
            {composerOpen && composerItem?.item_type === CHECKLIST_ITEM_TYPE.EMAIL_SENT && (
                <IosEmailComposer
                    open={composerOpen}
                    onClose={handleComposerClose}
                    practiceId={practiceId}
                    leadId={leadId}
                    leadDetail={leadData || { id: leadId }}
                    templateId={composerItem.action_config?.template_id || null}
                    onSendSuccess={handleComposerSend}
                />
            )}
            {composerOpen && composerItem?.item_type === CHECKLIST_ITEM_TYPE.WHATSAPP_SENT && (
                <IosWhatsAppComposerDialog
                    open={composerOpen}
                    onClose={handleComposerClose}
                    practiceId={practiceId}
                    leadId={leadId}
                    leadDetail={leadData || { id: leadId }}
                    phoneNumber={leadData?.lead_contact?.phone || leadData?.phone || ''}
                    onSendSuccess={handleComposerSend}
                    prefillWaTemplateId={waPrefill?.templateId ?? null}
                    prefillWaTemplateName={waPrefill?.templateName ?? null}
                    prefillWaItemId={waPrefill?.itemId ?? null}
                />
            )}
        </>
    );
}

TaskChecklistCard.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadData: PropTypes.object,
    onGroupsLoaded: PropTypes.func,
    // When true (Tasks tab), completed checklists render in their own
    // collapsible "Completed Task Checklists" section (closed by default).
    // When false (Overview), completed checklists are hidden entirely.
    showCompletedSeparately: PropTypes.bool,
    // Optional: called with { task, group } (the soonest-due open task) or null
    // whenever the loaded set changes — powers the Overview "Next step" card.
    // Pass a STABLE function (e.g. a useState setter) to avoid effect churn.
    onNextTaskResolved: PropTypes.func,
    // Optional: called with { [leadTreatmentId]: { total, open, done, nextOpenTask } }
    // whenever groups change — powers per-treatment task-aware Next Step cards in
    // the Overview. '_unscoped' key holds tasks with no linked treatment.
    onTreatmentTaskSummary: PropTypes.func,
    // Headless: still fetch + report (onNextTaskResolved/onGroupsLoaded) but
    // render no visible card. Used on the lead Overview, where the full card is
    // redundant with the Tasks tab.
    hideCard: PropTypes.bool,
    // Called exactly once after the first fetch (or feature-gate early-return)
    // settles — lets the parent suppress task-dependent UI until data is ready.
    onFirstFetchDone: PropTypes.func,
    // Group-by-treatment mode (Tasks tab): bucket the template groups +
    // standalone tasks into per-treatment collapsible sections with one global
    // completed collapse. Replaces the flat "Active/Completed checklists" cards.
    groupByTreatment: PropTypes.bool,
    // Standalone (non-template) OPEN tasks to interleave into the matching
    // treatment section (groupByTreatment mode).
    standaloneOpenTasks: PropTypes.array,
    // Standalone COMPLETED tasks for the global completed collapse.
    standaloneCompletedTasks: PropTypes.array,
    // (task) => ReactNode — renders a standalone task row (the parent owns the
    // TaskRow + its completeTask wiring). Required for standalone interleave.
    renderStandaloneRow: PropTypes.func,
    // Parent's task-list loading flag — folded into the grouped skeleton so we
    // don't flash an empty state while the parent's /tasks/ fetch is in flight.
    parentLoading: PropTypes.bool,
    selectedTreatmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

TaskChecklistCard.defaultProps = {
    leadData: null,
    onGroupsLoaded: () => {},
    showCompletedSeparately: false,
    onNextTaskResolved: null,
    onTreatmentTaskSummary: null,
    hideCard: false,
    onFirstFetchDone: null,
    groupByTreatment: false,
    standaloneOpenTasks: [],
    standaloneCompletedTasks: [],
    renderStandaloneRow: null,
    parentLoading: false,
    selectedTreatmentId: null,
};
